/**
 * Zoom and Display Configuration System
 * Centralized configuration for planet layer zoom thresholds and display modes
 */

import { FONTS } from '../../../../../config/fonts';

// Zoom configurations moved to unified ZoomConfig.ts
import {
    UNIFIED_ZOOM_CONSTANTS as ZOOM_CONSTANTS,
    UNIFIED_ZOOM_CONFIG,
    DEFAULT_ZOOM_BEHAVIOR_CONFIG,
    ZOOM_FONT_SIZE_CONFIG,
    ZOOM_RANGES,
    getDisplayModeForZoom,
    clampZoom,
    calculateInitialZoom,
    getZoomRangeInfo,
    getZoomModeDescription,
    getZoomBehaviorDescription,
    validateZoomConfig,
    ZoomBehaviorConfig,
    DisplayMode
} from './ZoomConfig';

// ZOOM_CONSTANTS re-exported below with other zoom configs

// ZoomBehaviorMode moved to ZoomConfig.ts

/**
 * Export types for external use
 */
// DisplayMode moved to ZoomConfig.ts
export type ZoomRange = typeof ZOOM_RANGES[keyof typeof ZOOM_RANGES];

// ZoomBehaviorConfig moved to ZoomConfig.ts

/**
 * Typography configuration for text rendering
 */
export interface TypographyConfig {
    fontFamily: string;             // Font family stack
    fontSize: number;               // Base font size
    fontWeight: number | string;    // Font weight (400, 500, 600, 700 or 'normal', 'bold')
    textColor: string;              // Text color
    textAlign: 'start' | 'middle' | 'end'; // Text alignment
    lineHeight: number;             // Line height multiplier
    letterSpacing: number;          // Letter spacing in px
    
    // Text stroke/outline settings
    stroke: string;                 // Text stroke color ('none' to disable)
    strokeWidth: number;            // Text stroke width in px
    strokeOpacity: number;          // Text stroke opacity (0-1)
    
    // Dynamic sizing
    enableDynamicSizing: boolean;   // Enable responsive font sizing
    baseContainerSize: number;      // Base container size for scaling
    minFontSize: number;            // Minimum font size
    maxFontSize: number;            // Maximum font size
}

/**
 * Planet display configuration interface
 */
export interface PlanetDisplayConfig {
    // Zoom thresholds
    symbolsZoomThreshold: number;    // Threshold for symbols → short
    fullZoomThreshold: number;       // Threshold for short → full
    zoomLevelDelta: number;          // Delta for zoom level generation (ensures mode switching)
    
    // Display modes
    symbolsDisplayMode: 'symbols';   // Low zoom display mode
    shortDisplayMode: 'short';       // Medium zoom display mode
    fullDisplayMode: 'full';         // High zoom display mode
    
    // Event handling
    zoomEventThrottle: number;       // Throttle zoom events (ms)
    
    // Typography settings
    typography: TypographyConfig;    // Typography configuration
    
    // Layout settings
    spacing: number;                // Vertical spacing between planets
    maxPlanetsPerRow: number;       // Max planets before wrapping
    showDegrees: boolean;           // Show degree values
    showRetrograde: boolean;        // Show retrograde indicators
    
    // Positioning
    baseX: number;                  // Left padding
    baseY: number;                  // Top padding
    
    // Default zoom
    defaultZoom: number;            // Initial zoom level
    
    // Zoom behavior
    zoomBehavior: ZoomBehaviorConfig; // Zoom behavior configuration
}

/**
 * Layer visibility configuration
 */
export interface LayerVisibilityConfig {
    // Layer states
    enableAutoHide: boolean;        // Auto-hide layers based on zoom
    fadeTransition: boolean;        // Use fade transitions
    transitionDuration: number;     // Transition duration (ms)
    
    // Performance
    visibilityDebounce: number;     // Debounce visibility changes (ms)
    maxVisibleLayers: number;       // Maximum simultaneously visible layers
}

/**
 * Complete zoom display configuration
 */
export interface ZoomDisplayConfig {
    planets: PlanetDisplayConfig;
    layers: LayerVisibilityConfig;
}

// Zoom constants moved to unified ZoomConfig.ts

// ZoomConfig interface moved to ZoomConfig.ts

// UNIFIED_ZOOM_CONFIG moved to ZoomConfig.ts

// DEFAULT_ZOOM_BEHAVIOR_CONFIG moved to ZoomConfig.ts

/**
 * Default typography configuration (inline - legacy TypographyConfig.ts removed)
 */
export const DEFAULT_TYPOGRAPHY_CONFIG: TypographyConfig = {
    fontFamily: FONTS.chart.primary,
    fontSize: 12,
    fontWeight: 500,
    textColor: '#333333',
    textAlign: 'middle',
    lineHeight: 1.2,
    letterSpacing: 0,
    stroke: 'none',
    strokeWidth: 0,
    strokeOpacity: 0,
    enableDynamicSizing: false,
    baseContainerSize: 150,
    minFontSize: 8,
    maxFontSize: 16
};

export const PLANET_TYPOGRAPHY_CONFIG: TypographyConfig = {
    ...DEFAULT_TYPOGRAPHY_CONFIG,
    fontSize: 12,
    fontWeight: 600,
    textColor: '#374151'
};

export const BACKGROUND_TYPOGRAPHY_CONFIG: TypographyConfig = {
    ...DEFAULT_TYPOGRAPHY_CONFIG,
    fontSize: 10,
    fontWeight: 400,
    textColor: '#6b7280'
};

export const LAGNA_TYPOGRAPHY_CONFIG: TypographyConfig = {
    ...DEFAULT_TYPOGRAPHY_CONFIG,
    fontSize: 12,
    fontWeight: 700,
    textColor: '#dc2626'
};

export const HORA_TYPOGRAPHY_CONFIG: TypographyConfig = {
    ...DEFAULT_TYPOGRAPHY_CONFIG,
    fontSize: 12,
    fontWeight: 600,
    textColor: '#2563eb'
};

// ZOOM_FONT_SIZE_CONFIG moved to ZoomConfig.ts

/**
 * Default planet display configuration
 */
export const DEFAULT_PLANET_DISPLAY_CONFIG: PlanetDisplayConfig = {
    // Zoom thresholds
    symbolsZoomThreshold: 0.5,      // Switch to short names at 1x
    fullZoomThreshold: 0.7,         // Switch to full names at 1.5x
    zoomLevelDelta: 0.01,            // Delta for zoom level generation (ensures mode switching)
    // Display modes
    symbolsDisplayMode: 'symbols',  // ☉☽♂☿♃♀♄☊☋
    shortDisplayMode: 'short',      // Sy Ch Ma Bu Gu Sk Sa Ra Ke
    fullDisplayMode: 'full',        // Surya Chandra Mangala...
    
    // Event handling
    zoomEventThrottle: 100,         // 100ms throttle for smooth performance
    
    // Typography settings
    typography: PLANET_TYPOGRAPHY_CONFIG,
    
    // Layout settings
    spacing: 32,                   // 12px vertical spacing
    maxPlanetsPerRow: 4,           // 4 planets per row max
    showDegrees: true,            // Hide degrees by default
    showRetrograde: true,          // Show retrograde indicators
    
    // Positioning
    baseX: 50,                     // 10px left padding
    baseY: 50,                     // 15px top padding
    
    // Default zoom
    defaultZoom: 1.0,              // Start at 1.0x zoom (short mode)
    
    // Zoom behavior
    zoomBehavior: DEFAULT_ZOOM_BEHAVIOR_CONFIG
};

/**
 * Default layer visibility configuration
 */
export const DEFAULT_LAYER_VISIBILITY_CONFIG: LayerVisibilityConfig = {
    // Layer states
    enableAutoHide: true,          // Enable auto-hide based on zoom
    fadeTransition: true,          // Use smooth fade transitions
    transitionDuration: 250,       // 250ms transition duration
    
    // Performance
    visibilityDebounce: 50,        // 50ms debounce for visibility changes
    maxVisibleLayers: 3            // Max 3 layers visible simultaneously
};

/**
 * Default complete zoom display configuration
 */
export const DEFAULT_ZOOM_DISPLAY_CONFIG: ZoomDisplayConfig = {
    planets: DEFAULT_PLANET_DISPLAY_CONFIG,
    layers: DEFAULT_LAYER_VISIBILITY_CONFIG
};

// ZOOM_RANGES moved to ZoomConfig.ts

// Export zoom configurations from unified system
export {
    ZOOM_CONSTANTS,
    UNIFIED_ZOOM_CONFIG,
    DEFAULT_ZOOM_BEHAVIOR_CONFIG,
    ZOOM_FONT_SIZE_CONFIG,
    ZOOM_RANGES,
    getDisplayModeForZoom,
    clampZoom,
    calculateInitialZoom,
    getZoomRangeInfo,
    getZoomModeDescription,
    getZoomBehaviorDescription,
    validateZoomConfig
};
export type { ZoomConfig, ZoomBehaviorMode, ZoomBehaviorConfig, DisplayMode } from './ZoomConfig';

/**
 * Configuration validation
 */
export function validateZoomDisplayConfig(config: {
    planets?: Partial<PlanetDisplayConfig>;
    layers?: Partial<LayerVisibilityConfig>;
}): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
} {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Validate planet config
    if (config.planets) {
        const p = config.planets;
        
        // Zoom thresholds validation
        if (p.symbolsZoomThreshold && p.fullZoomThreshold) {
            if (p.symbolsZoomThreshold >= p.fullZoomThreshold) {
                errors.push('symbolsZoomThreshold must be less than fullZoomThreshold');
            }
        }
        
        if (p.symbolsZoomThreshold && (p.symbolsZoomThreshold <= 0 || p.symbolsZoomThreshold > 2)) {
            warnings.push('symbolsZoomThreshold should be between 0 and 2');
        }
        
        if (p.fullZoomThreshold && (p.fullZoomThreshold <= 0.5 || p.fullZoomThreshold > 5)) {
            warnings.push('fullZoomThreshold should be between 0.5 and 5');
        }
        
        
        if (p.spacing && (p.spacing < 8 || p.spacing > 30)) {
            warnings.push('spacing should be between 8 and 30 pixels');
        }
        
        if (p.maxPlanetsPerRow && (p.maxPlanetsPerRow < 1 || p.maxPlanetsPerRow > 9)) {
            warnings.push('maxPlanetsPerRow should be between 1 and 9');
        }
        
        // Zoom behavior validation
        if (p.zoomBehavior) {
            const zb = p.zoomBehavior;
            
            if (zb.mode && !['fixed', 'fit'].includes(zb.mode)) {
                errors.push('zoomBehavior.mode must be either "fixed" or "fit"');
            }
            
            if (zb.fixedZoomLevel && (zb.fixedZoomLevel <= 0 || zb.fixedZoomLevel > 10)) {
                warnings.push('zoomBehavior.fixedZoomLevel should be between 0 and 10');
            }
            
            if (zb.fitPadding && (zb.fitPadding < 0 || zb.fitPadding > 50)) {
                warnings.push('zoomBehavior.fitPadding should be between 0 and 50 percent');
            }
            
            if (zb.minZoom && zb.maxZoom && zb.minZoom >= zb.maxZoom) {
                errors.push('zoomBehavior.minZoom must be less than maxZoom');
            }
            
            if (zb.minZoom && (zb.minZoom <= 0 || zb.minZoom > 1)) {
                warnings.push('zoomBehavior.minZoom should be between 0 and 1');
            }
            
            if (zb.maxZoom && (zb.maxZoom < 1 || zb.maxZoom > 20)) {
                warnings.push('zoomBehavior.maxZoom should be between 1 and 20');
            }
        }
    }
    
    // Validate layer config
    if (config.layers) {
        const l = config.layers;
        
        if (l.transitionDuration && (l.transitionDuration < 0 || l.transitionDuration > 1000)) {
            warnings.push('transitionDuration should be between 0 and 1000ms');
        }
        
        if (l.visibilityDebounce && (l.visibilityDebounce < 0 || l.visibilityDebounce > 500)) {
            warnings.push('visibilityDebounce should be between 0 and 500ms');
        }
        
        if (l.maxVisibleLayers && (l.maxVisibleLayers < 1 || l.maxVisibleLayers > 10)) {
            warnings.push('maxVisibleLayers should be between 1 and 10');
        }
    }
    
    return {
        isValid: errors.length === 0,
        errors,
        warnings
    };
}

// getDisplayModeForZoom moved to ZoomConfig.ts

// getZoomRangeInfo moved to ZoomConfig.ts

/**
 * Configuration manager class
 */
export class ZoomDisplayConfigManager {
    private config: ZoomDisplayConfig;
    private listeners: ((config: ZoomDisplayConfig) => void)[] = [];
    
    constructor(initialConfig: Partial<ZoomDisplayConfig> = {}) {
        this.config = {
            planets: { ...DEFAULT_PLANET_DISPLAY_CONFIG, ...initialConfig.planets },
            layers: { ...DEFAULT_LAYER_VISIBILITY_CONFIG, ...initialConfig.layers }
        };
    }
    
    /**
     * Get current configuration
     */
    getConfig(): ZoomDisplayConfig {
        return { ...this.config };
    }
    
    /**
     * Update configuration
     */
    updateConfig(updates: {
        planets?: Partial<PlanetDisplayConfig>;
        layers?: Partial<LayerVisibilityConfig>;
    }): void {
        const validation = validateZoomDisplayConfig(updates);
        
        if (!validation.isValid) {
            throw new Error(`Invalid configuration: ${validation.errors.join(', ')}`);
        }
        
        if (validation.warnings.length > 0) {
            // // devWarn('[ZoomDisplayConfig] Configuration warnings:', validation.warnings);
        }
        
        this.config = {
            planets: { ...this.config.planets, ...updates.planets },
            layers: { ...this.config.layers, ...updates.layers }
        };
        
        // Notify listeners
        this.listeners.forEach(listener => listener(this.config));
    }
    
    /**
     * Subscribe to configuration changes
     */
    subscribe(listener: (config: ZoomDisplayConfig) => void): () => void {
        this.listeners.push(listener);
        
        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }
    
    /**
     * Reset to defaults
     */
    resetToDefaults(): void {
        this.config = { ...DEFAULT_ZOOM_DISPLAY_CONFIG };
        this.listeners.forEach(listener => listener(this.config));
    }
}

/**
 * Global configuration manager instance
 */
export const globalZoomDisplayConfig = new ZoomDisplayConfigManager();

// calculateInitialZoom moved to ZoomConfig.ts

/**
 * Check if zoom level is within allowed bounds
 */
export function isZoomWithinBounds(
    zoom: number,
    zoomBehavior: ZoomBehaviorConfig = DEFAULT_ZOOM_BEHAVIOR_CONFIG
): boolean {
    return zoom >= zoomBehavior.minZoom && zoom <= zoomBehavior.maxZoom;
}

// clampZoom moved to ZoomConfig.ts

/**
 * Generate zoom levels based on planet display configuration
 */
export function generatePlanetZoomLevels(config: PlanetDisplayConfig = DEFAULT_PLANET_DISPLAY_CONFIG): {
    levels: number[];
    modes: DisplayMode[];
    thresholds: { symbols: number; full: number };
} {
    const symbolsThreshold = config.symbolsZoomThreshold;
    const fullThreshold = config.fullZoomThreshold;
    const delta = config.zoomLevelDelta; // Configurable delta to ensure mode switching
    
    return {
        levels: [
            symbolsThreshold - delta,                    // Level 1: symbols mode (just below symbolsZoomThreshold)
            symbolsThreshold + delta,                    // Level 2: short mode (just above symbolsZoomThreshold)
            fullThreshold + delta                        // Level 3: full mode (just above fullZoomThreshold)
        ],
        modes: ['symbols', 'short', 'full'],
        thresholds: {
            symbols: symbolsThreshold,
            full: fullThreshold
        }
    };
}

// getZoomModeDescription moved to ZoomConfig.ts


